import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
// No MUI here. This screen is now plain DS + Tailwind: @mui/material's Card/Paper/Box
// and @mui/lab's Timeline were the whole reason it read as a document rather than a
// screen — and @mui/lab was imported by only this file and ActivityLog (now deleted).
import { useAuth } from "../../../contexts/AuthContext";
import { services } from "../../../services/api";
import { getDeviceFingerprint } from "../../../utils/security";
import AlertMessage from "../Components/AlertMessage";
import { useToast, Icon } from "../../../components/ds";
import brandMark from "../../../data/logo-icon-light.png";

/**
 * BrokerSessionCallback Component
 *
 * This component is rendered when a user is redirected back from a broker's authentication page.
 * It processes the URL parameters to extract:
 * - broker session code
 * - userid
 * - broker account id
 *
 * Then it sends these details to the API Gateway to finalize the broker session setup.
 *
 * This component is now part of the BrokerPlatforms module and integrates with BrokerLoginStep
 * to provide status updates for the login process.
 */
const BrokerSessionCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { isAuthenticated, user, updateUserContext } = useAuth();

  // State for session details from URL
  const [sessionDetails, setSessionDetails] = useState({
    userId: "",
    brokerAccountId: "",
    sessionCode: "",
  });

  // Connection and processing states
  const [isConnecting, setIsConnecting] = useState(true);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [error, setError] = useState(null);

  // Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // Use ref to ensure callback processing only occurs once (avoids double-run in StrictMode)
  const didProcessRef = useRef(false);

  //--> DS toast: bridge the legacy snackbar triggers (setShowSnackbar) to useToast.
  const toast = useToast();
  useEffect(() => {
    if (showSnackbar && snackbarMessage) {
      (toast[snackbarSeverity] || toast.info)(snackbarMessage);
      setShowSnackbar(false);
    }
  }, [showSnackbar, snackbarMessage, snackbarSeverity, toast]);

  // Show a snackbar notification
  const showSnackbarNotification = (
    message,
    severity = "info",
    duration = 6000
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setShowSnackbar(true);

    // Optionally automatically hide the snackbar after duration
    if (duration) {
      setTimeout(() => {
        setShowSnackbar(false);
      }, duration);
    }
  };

  // Function to update user context with broker connection status
  const updateUserBrokerStatus = (brokerDetails) => {
    if (updateUserContext && typeof updateUserContext === "function") {
      updateUserContext({
        brokerConnections: {
          ...(user?.brokerConnections || {}),
          [brokerDetails.brokerAccountId]: {
            connected: true,
            brokerName: brokerDetails.brokerName,
            timestamp: Date.now(),
          },
        },
      });
    }
  };

  // Return to Broker Accounts. This page is almost always a tab opened by window.open
  // from the connect flow, so the cleanest exit is to CLOSE it — the original tab has
  // already updated over the WebSocket, so the user lands back there with the account
  // live. window.close() only works on a script-opened tab; if this page was opened
  // directly it stays put, so we navigate to the accounts page as a fallback.
  // (The old target "/broker-platforms" is not a route — it fell through to the
  //  dashboard, which is why "Back to Broker Accounts" did not go to Broker Accounts.)
  const handleBackToAccounts = () => {
    window.close();
    setTimeout(() => navigate("/TradeAccounts"), 150);
  };

  // Auto-return, but ONLY on a clean success — a failure MUST stay on the page so the
  // user sees what went wrong and can retry. Two guards, deliberately:
  //   - isSuccess AND !error: setIsSuccess(true) runs before a few more lines
  //     (localStorage, context) that could throw into the catch, which sets `error`
  //     while isSuccess is already true. Requiring !error means any error wins and the
  //     page stays, even in that success-then-threw window.
  //   - `error` is in the deps, so if an error lands within the hold, this re-runs,
  //     the guard fails, and the cleanup clears the timers — the tab does not close out
  //     from under a failure.
  // `returnIn` is the visible countdown on the button (5 → 0). It is DISPLAY only; a
  // separate timer does the actual return at 0, so a slow render can't delay the return
  // or let the two drift.
  const [returnIn, setReturnIn] = useState(null);
  useEffect(() => {
    if (!isSuccess || error) {
      setReturnIn(null);
      return undefined;
    }
    const seconds = 5;
    setReturnIn(seconds);
    const tick = setInterval(
      () => setReturnIn((n) => (n > 0 ? n - 1 : 0)),
      1000
    );
    const done = setTimeout(() => handleBackToAccounts(), seconds * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(done);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, error]);

  // Function to update broker session status for BrokerLoginStep polling
  const updateBrokerSessionStatus = (status, data = null, error = null) => {
    const brokerAccountId = params.brokerAccountId || sessionDetails.brokerAccountId;
    const statusData = {
      status,
      timestamp: Date.now(),
      brokerAccountId: brokerAccountId,
      data,
      error,
    };

    console.log("🔄 Updating broker session status:", {
      status,
      brokerAccountId,
      statusData
    });

    // Store in localStorage (and sessionStorage as backup) for the original tab to
    // read. localStorage IS shared across tabs, so this is the real cross-tab signal —
    // the connect dialog's poll and BrokerLoginStep's mount read both pick it up.
    localStorage.setItem("brokerLoginStatus", JSON.stringify(statusData));
    sessionStorage.setItem("brokerLoginStatus", JSON.stringify(statusData));

    // (The window CustomEvent that used to fire here was removed: this callback runs in
    // its own window.open tab, and a CustomEvent cannot cross to the tab that opened it,
    // so no listener ever received it.)
  };

  // Function to extract URL parameters and submit session data to API Gateway
  useEffect(() => {
    if (didProcessRef.current) return;
    didProcessRef.current = true;

    const processCallbackParams = async () => {
      try {
        // Update status to processing
        updateBrokerSessionStatus("processing");

        // Extract URL parameters from the new URL format:
        // https://<deetrade-website>/callback/<userid>/<broker-account-id>?code=<broker-session-code>

        // Extract userId and brokerAccountId from URL path parameters
        const userId = params.userId;
        const brokerAccountId = params.brokerAccountId;

        // Extract session code from query string
        const searchParams = new URLSearchParams(location.search);
        const queryParams = {};
        searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });

        if (!userId || !brokerAccountId) {
          throw new Error("Missing required parameters in callback URL");
        }

        // Get device fingerprint
        const deviceFingerprint = getDeviceFingerprint();

        // Create session details object directly instead of relying on state update
        const sessionDetailsObj = {
          user_id: userId,
          broker_account_id: brokerAccountId,
          paramDetails: queryParams,
          device_fingerprint: deviceFingerprint,
        };

        // Update state for display purposes
        setSessionDetails(sessionDetailsObj);
        setIsConnecting(false);

        // Submit the session key to the API Gateway
        // The gateway will validate and process the session details internally
        // const sessionResponse = await services.brokerAccounts.submitSessionDetails(
        console.log("About to call submitSessionDetails via brokerAccounts service");
        const sessionResponse = await services.brokerAccounts.submitSessionDetails(
          brokerAccountId,
          sessionDetailsObj
        );
        console.log("Response from submitSessionDetails:", sessionResponse);
        console.log("Response status:", sessionResponse?.data?.status);
        console.log("Response data:", sessionResponse?.data?.data);
        
        // Check if the session was successfully processed
        // Fix: Check the correct response structure
        if (sessionResponse?.data?.status !== "success") {
          const errorMsg = sessionResponse?.data?.message || "Failed to validate broker session";
          console.error("Session validation failed. Expected 'success' but got:", sessionResponse?.data?.status);
          console.error("Full response:", sessionResponse);
          throw new Error(errorMsg);
        }

        // Process customer details - update to use correct response structure
        if (sessionResponse?.data?.data) {
          // Set the customer details from the session response
          setCustomerDetails(sessionResponse.data?.data || {});
          setIsSuccess(true);

          // Prepare the broker connection data
          const brokerConnectionData = {
            success: true,
            brokerAccountId,
            brokerName: sessionResponse.data.data?.broker_name || "Broker",
            customerName: sessionResponse.data.data?.customer_details?.name || "",
            timestamp: Date.now(),
          };

          // Update broker session status for BrokerLoginStep
          updateBrokerSessionStatus("completed", brokerConnectionData);

          // Store successful login status in localStorage for use by other components
          localStorage.setItem(
            "brokerSessionStatus",
            JSON.stringify(brokerConnectionData)
          );

          // Update user context with broker connection status
          updateUserBrokerStatus(brokerConnectionData);

          // Show success message
          showSnackbarNotification(
            `Successfully connected to ${
              sessionResponse.data.data?.broker_name || "broker"
            } account`,
            "success"
          );
        } else {
          throw new Error("Invalid or empty response data received");
        }
      } catch (error) {
        console.error("Error processing broker callback:", error);
        setError(
          error.message || "Failed to process broker authentication callback"
        );

        // Update broker session status with error for BrokerLoginStep
        updateBrokerSessionStatus("failed", null, error.message);

        // Store error status in localStorage for use by other components
        localStorage.setItem(
          "brokerSessionStatus",
          JSON.stringify({
            success: false,
            brokerAccountId: params.brokerAccountId || "",
            error: error.message || "Connection failed",
            timestamp: Date.now(),
          })
        );

        // Show error message
        showSnackbarNotification(
          error.message || "Failed to connect to broker account",
          "error"
        );
      } finally {
        setIsProcessing(false);
      }
    };

    // Only process URL if the page just loaded
    processCallbackParams();
  }, [location.search, params]);

  // The broker's name, for copy that names what the user is actually connecting.
  // customerDetails.broker_name is the raw internal slug ("icici_direct", "flattrade"),
  // so a naive capitalise produced "Icici_direct" in the headline. These are brand
  // names — a user reads "ICICI Direct", never "Icici_direct".
  const BROKER_LABELS = {
    icici_direct: "ICICI Direct",
    flattrade: "Flattrade",
    papertrade: "Paper Trading",
  };
  const rawBroker = customerDetails?.broker_name;
  const brokerName = rawBroker
    ? BROKER_LABELS[rawBroker.toLowerCase()] ||
      // Unknown broker: at least don't show the slug's underscores.
      rawBroker.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "your broker";

  // Step 1 was a TimelineDot hardcoded to color="success" — it reported "Received
  // authentication data" as succeeded even when the callback URL had no params and
  // the effect threw "Missing required parameters". It now reflects what happened.
  // These conditions are a 1:1 REPLICA of the original Timeline's, which worked. Only
  // the presentation below is new.
  //
  //   step 1  <TimelineDot color="success" />                       -> always done
  //   step 2  isConnecting ? dot"secondary" : dot"success"          -> active : done
  //   step 3  isProcessing ? dot"secondary"
  //           : isSuccess  ? dot"success" : dot"error"              -> active : done : error
  //
  // I previously replaced these with a gate of my own invention
  // (`sessionDetails.sessionCode`), read from the useState declaration — a key the
  // only setter never writes. It was permanently false, so step 1 froze at "Waiting"
  // while the headline said "You're connected". The state this component actually
  // runs on is isConnecting / isProcessing / isSuccess, and it was already correct.
  // Don't re-derive working logic to change a skin.
  const steps = [
    {
      key: "received",
      state: "done",
      done: `Signed in with ${brokerName}`,
    },
    {
      key: "secured",
      state: isConnecting ? "active" : "done",
      active: "Securing your session",
      done: "Session secured",
    },
    {
      key: "connected",
      state: isProcessing ? "active" : isSuccess ? "done" : "error",
      active: "Connecting your account",
      done: `${brokerName} is connected`,
      error: "Couldn't connect the account",
    },
  ];

  const headline = isProcessing
    ? `Connecting ${brokerName}`
    : isSuccess
    ? "You're connected"
    : `Couldn't connect ${brokerName}`;

  const subline = isProcessing
    ? "This only takes a moment — you can stay on this screen."
    : isSuccess
    ? "Your trading session is live. You can head back and start trading."
    : "Nothing was changed on your account. You can try connecting again.";

  return (
    // Phone: full-bleed on the page canvas, vertically centred — a native screen,
    // not a document. sm+: a single contained card, which is what a browser wants.
    // (It was two stacked MUI cards + a Paper: three surfaces to say one thing.)
    <div className="min-h-[100dvh] bg-bg-1 flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md sm:bg-bg-2 sm:border sm:border-[var(--divider)] sm:rounded-2xl sm:shadow-sm sm:p-8">
        {/* Subtle brand: enough to say "this is still DeeTrade" while the broker
            hands you back, without turning a transient screen into a billboard. */}
        <div className="flex items-center gap-2 mb-8">
          <img src={brandMark} alt="" className="w-6 h-6 rounded-md" aria-hidden="true" />
          <span className="text-sm font-semibold tracking-tight text-fg-2">DeeTrade</span>
        </div>

        <h1 className="ds-h2 text-fg-1 mb-1">{headline}</h1>
        <p className="text-sm text-fg-2 mb-8 leading-relaxed">{subline}</p>

        {/* The timeline, kept — it is the good part of this screen. Rebuilt as a
            plain left-aligned list: @mui/lab's <Timeline position="alternate">
            zigzagged and reserved a flex:1 gutter opposite every item, so at 343px
            the text column collapsed to ~110px and wrapped 4-5 lines a step. */}
        <ol className="relative">
          {steps.map((step, i) => {
            const last = i === steps.length - 1;
            const label = step[step.state] || step.done;
            const tone =
              step.state === "done"
                ? "text-fg-1"
                : step.state === "active"
                ? "text-fg-1"
                : step.state === "error"
                ? "text-loss"
                : "text-fg-3";
            return (
              <li key={step.key} className="flex gap-3">
                {/* rail: dot + connector */}
                <div className="flex flex-col items-center shrink-0">
                  <span
                    className={`grid place-items-center w-6 h-6 rounded-full shrink-0 ${
                      step.state === "done"
                        ? "bg-profit text-white"
                        : step.state === "error"
                        ? "bg-loss text-white"
                        : step.state === "active"
                        ? "bg-accent/15 text-accent"
                        : "bg-bg-3 text-fg-3"
                    }`}
                  >
                    {step.state === "done" ? (
                      <Icon name="success" size={14} />
                    ) : step.state === "error" ? (
                      <Icon name="error" size={14} />
                    ) : step.state === "active" ? (
                      <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-fg-3" />
                    )}
                  </span>
                  {/* The connector was `flex-1` with the step spacing coming from the
                      li's pb-6 (padding, OUTSIDE the flex content box) — so flex-1 had
                      no height to fill and the line rendered 0px: three ticks, no line.
                      The min-height gives it real height (and drives the inter-step gap
                      now that pb-6 is gone); flex-1 still lets it grow when the active
                      step's progress bar makes the row taller. */}
                  {!last && (
                    <span
                      className={`w-px flex-1 min-h-[1.75rem] my-1.5 ${
                        step.state === "done" ? "bg-profit/40" : "bg-[var(--divider)]"
                      }`}
                    />
                  )}
                </div>

                <div className="min-w-0 pt-0.5">
                  <div className={`text-sm font-medium ${tone}`}>{label}</div>
                  {step.state === "active" && (
                    <div className="mt-2 h-0.5 w-24 rounded-full bg-bg-3 overflow-hidden">
                      <span className="block h-full w-1/2 rounded-full bg-accent animate-pulse" />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {/* severity, NOT type. AlertMessage's prop is `severity` and it defaults to
            "info" — the old `type="error"` was spread into MUI's Alert as an unknown
            prop and ignored, so every failure on this page has been rendering as a
            calm blue info box. This file was the only place in the app misusing it. */}
        {error && (
          <div className="mt-6">
            <AlertMessage severity="error" message={error} />
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary w-full mt-8"
          onClick={handleBackToAccounts}
        >
          <Icon name="back" size={16} />
          Back to Broker Accounts
          {/* The auto-return countdown, so the user knows it is coming and how long
              they have to read the screen. Only on a clean success (returnIn is null
              on failure — see the effect above), and tabular-nums so the width does
              not jitter as it ticks. */}
          {returnIn != null && returnIn > 0 && (
            <span className="ml-1 opacity-70 tabular-nums">· {returnIn}s</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default BrokerSessionCallback;
